import { formatDate } from "@angular/common";
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Address } from "@models/address/address";
import { AddressStatus } from '@models/address/address-status.enums';
import { AnnualStatement } from "@models/annual-statements/annual-statement";
import { Contact } from "@models/common/contact";
import { Company } from "@models/companies/company";
import { Attendee } from '@models/documents/attendee';
import { ChangeAuthorisation } from '@models/documents/change-authorisation';
import {
  DocumentationType,
  MeetingLocationType,
  MeetingOnlineType,
  MinutesMeetingPresence
} from '@models/documents/document-enums';
import { IndividualData } from "@models/individuals/individual-data";
import { Relationship } from "@models/relationships/relationship";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { SelectOption } from '@models/ui-models/select-options';
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import {
  AgDirectorMeetingStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-director-meeting-status/ag-director-meeting-status.component";
import { BaseListGridComponent } from '@shared/components/grid/components/base-list-grid/base-list-grid.component';
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { AnnualStatementsService } from "@shared/services/annual-statements.service";
import { autocompleteServiceToken } from "@shared/services/autocomplete.service";
import { AuxiliaryApiService } from "@shared/services/auxiliary-api.service";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { ContactsApiService } from "@shared/services/contacts-api.service";
import { UsersApiService } from "@shared/services/users-api.service";
import { Guid } from "@shared/utils/guid.helper";
import { DatePickerFormValidators, DatepickerHelper } from '@shared/validators/date-picker-form-validators';
import { ColDef, GridApi, RowSelectionOptions } from "ag-grid-community";
import { deepClone } from "fast-json-patch/commonjs/core";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ToastrService } from 'ngx-toastr';
import { catchError, debounceTime, filter, firstValueFrom, of, Subject, switchMap } from "rxjs";
import {
  AddressFormGroupComponent
} from "../../../../../forms/reusable-form-groups/address-form-group/address-form-group.component";
import { getDocumentationTypeOptions } from "../../../../../forms/utils/get-documentation-type-options";

@Component({
  selector: 'app-annual-statement-authorization',
  standalone: true,
  imports: [
    RadioComponent,
    FormsModule,
    ReactiveFormsModule,
    SelectComponent,
    DatePickerComponent,
    CheckboxComponent,
    AddressFormGroupComponent,
    NgxSkeletonLoaderModule,
    ListGridClientSideComponent
  ],
  providers: [
    {provide: autocompleteServiceToken, useClass: AuxiliaryApiService},
    CompaniesApiService
  ],
  templateUrl: 'annual-statement-authorization.component.html',
  styleUrl: 'annual-statement-authorization.component.scss'
})
export class AnnualStatementAuthorizationComponent implements OnInit {
  private annualStatementsService = inject(AnnualStatementsService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private companiesApiService = inject(CompaniesApiService);
  private toastr = inject(ToastrService);
  private contactsApiService = inject(ContactsApiService);
  private usersService = inject(UsersApiService);

  @Input() annual: AnnualStatement | undefined;
  @Input() company!: Company;
  @Input() confirmAction$: Subject<boolean> | undefined;

  @Output() nextStep = new EventEmitter<boolean>();

  readonly DocumentationType = DocumentationType;
  readonly MeetingLocationTypeEnum = MeetingLocationType;
  readonly customDatepickerErrors = DatePickerFormValidators.errorMessages;
  readonly attendeesRowSelection: RowSelectionOptions<any> = BaseListGridComponent.noCheckboxesRowSelection;

  gridApi!: GridApi;
  colDefs: ColDef[] = [];

  authorisationModel!: ChangeAuthorisation;
  expandedAddressForm = false;
  chairOptions: SelectOption[] = [];
  currentOfficerList: Relationship[] = [];
  attendeesList: Attendee[] = [];
  addressStatus: AddressStatus | undefined;
  partnerManagerOptions: SelectOption[] = [];

  setChairPersonAsSigningOfficer = false;
  setParnerManagerToCompany = false;

  minDateOfMeeting: NgbDateStruct = DatepickerHelper.getDefaultMinDateStruct();
  maxDateOfMeeting: NgbDateStruct = DatepickerHelper.getNextNMonthStruct(2);

  documentTypeOptions: SelectOption[] = getDocumentationTypeOptions(DocumentationType.FormOnly)

  meetingTypeOptions: SelectOption[] = [
    { label: 'Registered Address', value: MeetingLocationType.Registered },
    { label: 'Principal Address', value: MeetingLocationType.Principal },
    { label: 'Other address', value: MeetingLocationType.Other },
    { label: 'Phone', value: MeetingLocationType.Phone },
    { label: 'Online', value: MeetingLocationType.Online },
  ];

  meetingOnlineTypeOptions: SelectOption[] = [
    { label: 'Microsoft Teams', value: MeetingOnlineType.MicrosoftTeams },
    { label: 'Zoom', value: MeetingOnlineType.Zoom },
    { label: 'Google Meet', value: MeetingOnlineType.GoogleMeet }
  ];

  relationshipContactList: Contact[] = [];

  form = new FormGroup({
    address: AddressFormGroupComponent.defineForm(),
    partnerManager: new FormControl<string | null>(null, [Validators.required])
  });

  async ngOnInit(): Promise<void> {
    this.currentOfficerList = (this.company?.currentOfficers ?? [])
      .map((relationship) => {
        relationship.individualDataOverride = new IndividualData(relationship.individualDataOverride!)
        return relationship
      });

    await this.getRelationshipContactList();
    await this.loadPartnerManagerOptions();

    this.initForm();
    this.setFormSelectOptions();
    this.setGridConfig();
    this.listenHeaderButton();
    this.listenPartnerManager();
    this.setDisableHeaderBtn();
  }

  onDisableHeaderBtn(): void {
    this.annualStatementsService.disabledHeaderBtn.set(!this.validateForm());
  }

  initForm(): void {
    this.authorisationModel = this.annual?.changeAuthorisation ?? new ChangeAuthorisation();
    if(!this.authorisationModel.authorisingParty) {
      this.authorisationModel.authorisingParty = new Relationship();
    }

    if(this.authorisationModel.meetingAddressType === null || this.authorisationModel.meetingAddressType === undefined) {
      this.authorisationModel.meetingAddressType = MeetingLocationType.Registered;
    }

    if(!this.authorisationModel.meetingDate) {
      this.authorisationModel.meetingDate = new Date();
    }

    let partnerManagerId = '';
    if(this.authorisationModel.partnerManagerId && this.authorisationModel.partnerManagerId != Guid.EmptyGuid) {
      partnerManagerId = this.authorisationModel.partnerManagerId;
    } else {
      const partnerManager = this.partnerManagerOptions
        ?.find(o => o.value === this.annual?.company.partnerManagerId);
      partnerManagerId = partnerManager?.value as string ?? '';
    }

    this.form.patchValue({
      address: this.authorisationModel.meetingAddress,
      partnerManager: partnerManagerId
    });
  }

  setFormSelectOptions(): void {
    this.attendeesList = this.authorisationModel?.attendees?.map(attendee => ({
      officer: new Relationship({ individualDataOverride: attendee.individual }),
      presence: attendee.presence,
      checked: false
    })) as Attendee[];

    this.chairOptions = this.getOfficers(false, false)?.map(chair => {
      chair.contact = this.relationshipContactList.find(c => c.contactId === chair.contactId);
      const type = chair.type;
      const contactlabel =  chair.contact ? `(Сontact: ${chair.contact.email}; ${chair.contact.phone})` : '';
      const label = chair.individualDataOverride?.fullNameLabel + ` (${RelationshipType[type]})` + contactlabel;
      return ({ label, value: chair.relationshipId });
    }).sort((a, b) => a.label.localeCompare(b.label));

    if(!this.authorisationModel.chairperson?.relationshipId && this.authorisationModel.chairperson && this.chairOptions.length) {
      this.authorisationModel.chairperson.relationshipId = this.chairOptions[0].value as string;
    }

    if(this.authorisationModel.documentationType) {
      if (this.chairOptions.length <= 1) {
        this.documentTypeOptions = this.documentTypeOptions
          .filter(o => o.value !== DocumentationType.Minutes);

        this.authorisationModel.documentationType = DocumentationType.Resolution;
      } else {
        if (!this.documentTypeOptions.some(o => o.value === DocumentationType.Minutes)) {
          this.documentTypeOptions.push({
            label: 'Minutes',
            value: DocumentationType.Minutes
          });
        }
        this.authorisationModel.documentationType = DocumentationType.Minutes;
      }
      this.documentTypeOptions = [...this.documentTypeOptions];
    }
  }

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
  }

  setGridConfig(): void {
    this.colDefs = [
      {
        headerName: 'Directors who attended the meeting',
        field: 'officer.individualDataOverride.fullName',
        width: 296,
        flex: 2,
        editable: false,
        sort: 'asc',
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Status',
        field: 'presence',
        width: 80,
        flex: 0.7,
        sortable: false,
        cellRenderer: AgDirectorMeetingStatusComponent,
        cellRendererParams: {
          onSelectDirectorMeetingStatus: this.onSelectDirectorMeetingStatus.bind(this),
        },
        cellClass: 'actions-button-cell',
      },
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));
  }

  listenHeaderButton(): void {
    this.confirmAction$?.pipe(
      filter(confirm => confirm),
      switchMap(() => this.updateSigningContact()),
      switchMap(() => this.updatePatnerManagerContact()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      void this.confirm();
      this.confirmAction$?.next(false);
    });
  }

  updateSigningContact() {
    if(!this.setChairPersonAsSigningOfficer || !this.authorisationModel.chairperson?.contactId)
      return of(false);

    return this.companiesApiService.setSigningContact(this.company?.entityId, this.authorisationModel.chairperson?.contactId)
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError (() => {
        this.toastr.error('Error updating signing contact.');
        return of(false);
      })
    );
  }

   updatePatnerManagerContact() {
    if(!this.setParnerManagerToCompany || !this.authorisationModel.partnerManagerId)
      return of(false);

    return this.companiesApiService.updatePartnerManager(this.company?.entityId, this.authorisationModel.partnerManagerId)
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError (() => {
        this.toastr.error('Error updating partner manager.');
        return of(false);
      })
    );
  }

  listenPartnerManager(): void {
    this.form.controls.partnerManager.valueChanges
      .pipe(
        debounceTime(200),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.setDisableHeaderBtn());
  }

  confirm(): void {
    if (!this.validateForm()) {
      return;
    }

    if(this.annual?.changeAuthorisation) {
      this.annual.changeAuthorisation = { ...this.authorisationModel };
      this.annual.changeAuthorisation.meetingAddress = this.form.controls.address.value as Address;
      this.annual.changeAuthorisation.partnerManagerId = this.form.controls.partnerManager.value == "" ? null : this.form.controls.partnerManager.value;
      this.annual.changeAuthorisation.partnerManagerFullName = this.partnerManagerOptions.find(o => o.value === this.form.controls.partnerManager.value)?.label ?? null;
    }

    this.nextStep.emit(true);
  }

  onSelectDirectorMeetingStatus(): void {
    this.attendeesList.forEach((item, index) => {
      if(this.authorisationModel.attendees) {
        this.authorisationModel.attendees[index].presence = item.presence;
      }
    });

    this.setDisableHeaderBtn();
  }

  onDocumentationTypeChange(): void {
    this.setDisableHeaderBtn();
  }

  onChairPersonSelect(chairPersonId: string): void {
    const chair = this.getOfficers(false, false)
      .find(o => o.relationshipId === chairPersonId);

    if(chair && this.authorisationModel.chairperson) {
      this.authorisationModel.chairperson = deepClone(chair);
    }

    if(!this.authorisationModel.chairperson?.contactId) {
      this.setChairPersonAsSigningOfficer = false;
    }

    if (this.authorisationModel.authorisingParty && !this.authorisationModel?.authorisingParty?.relationshipId) {
      this.authorisationModel.authorisingParty.relationshipId = chairPersonId;
    }

    this.setDisableHeaderBtn();
  }

  onMeetingDateChange(meetingDate: Date | NgbDateStruct | string): void {
    if(meetingDate) {
      const date = meetingDate instanceof Date ? meetingDate : typeof meetingDate === 'string' ? new Date(meetingDate) : DatepickerHelper.getDateFromStruct(meetingDate);
      const serverDateFormat = formatDate(date, 'yyyy-MM-dd', 'en-US');
      if(this.annual?.changeAuthorisation) {
        this.annual.changeAuthorisation.meetingDate = serverDateFormat as unknown as Date;
      }
    }

    this.setDisableHeaderBtn();
  }

  setDisableHeaderBtn(): void {
    this.annualStatementsService.disabledHeaderBtn.set(!this.validateForm());
  }

  onDataStatusChange(addressStatus: AddressStatus | undefined): void {
    this.addressStatus = addressStatus;
    if(addressStatus === AddressStatus.WARNING) {
      this.annualStatementsService.disabledHeaderBtn.set(true);
    } else {
      this.annualStatementsService.disabledHeaderBtn.set(!this.validateForm());
    }
  }

  onMeetingAddressTypeSelect(type: MeetingLocationType): void {
      if(type === MeetingLocationType.Other) {
        this.form.controls.address.patchValue(this.annual?.changeAuthorisation?.meetingAddress ?? new Address());
        this.annualStatementsService.disabledHeaderBtn.set(true);
        return;
      }

      if(type === MeetingLocationType.Registered) {
        this.form.controls.address.patchValue(this.company?.registeredAddress);
      } else if(type === MeetingLocationType.Principal) {
        this.form.controls.address.patchValue(this.company?.principalAddress);
      } else {
        this.form.controls.address.patchValue(new Address());
      }

      this.addressStatus = undefined;

      this.onDisableHeaderBtn();
    }

  private validateForm(): boolean {
    const {
      attendees,
      documentationType,
      chairperson,
      meetingAddressType,
      meetingDate,
      meetingOnlineAddress,
      meetingAddress,
    } = this.authorisationModel;

    const isPartnerManagerValid = this.form.controls.partnerManager.valid as boolean;

    if (documentationType === DocumentationType.Minutes) {
      const isChairpersonValid = !!chairperson?.relationshipId;
      const isMeetingDateValid = !!meetingDate && meetingDate >= DatepickerHelper.getDateFromStruct(this.minDateOfMeeting);
      const isMeetingAddressValid = meetingAddress !== null && meetingAddress !== undefined;
      const isMeetingOnlineAddressValid = meetingOnlineAddress !== null && meetingOnlineAddress !== undefined;
      const isSomeAttendeesPresent = !!attendees?.some(a => a.presence !== MinutesMeetingPresence.Apology);

      if(meetingAddressType === MeetingLocationType.Registered || meetingAddressType === MeetingLocationType.Principal || meetingAddressType === MeetingLocationType.Other) {
        return isChairpersonValid && isMeetingDateValid && isMeetingAddressValid && isSomeAttendeesPresent && isPartnerManagerValid;
      } else if(meetingAddressType === MeetingLocationType.Online) {
        return isChairpersonValid && isMeetingDateValid && isMeetingOnlineAddressValid && isSomeAttendeesPresent && isPartnerManagerValid;
      } else if (meetingAddressType === MeetingLocationType.Phone) {
        return isChairpersonValid && isMeetingDateValid && isSomeAttendeesPresent && isPartnerManagerValid;
      }
    }

    return documentationType === DocumentationType.Resolution && isPartnerManagerValid;
  }

  get showChairpersonCheckbox(): boolean {
    return (this.relationshipContactList.length != 0 && this.authorisationModel.chairperson?.contactId != null && this.company.signingContactId == null);
  }


  getOfficers(includeALt = true, icludeSec = true) {
    let officersSelected: Relationship[] = [];
    const includedTypes: RelationshipType[] = [RelationshipType.Director];

    if (includeALt) {
      includedTypes.push(RelationshipType.AlternativeDirector);
    }
    if (icludeSec) {
      includedTypes.push(RelationshipType.Secretary);
    }

    officersSelected = this.currentOfficerList.filter(
      x => x.type != undefined && includedTypes.includes(x.type)
    );

    return officersSelected;
  }

  async loadPartnerManagerOptions() {
    const users = await firstValueFrom(this.usersService.getAssignableUsersCached());
    this.partnerManagerOptions = users.map(profile => ({
      label: profile.fullName,
      value: profile.id
    }));
  }

  async getRelationshipContactList() {

    const officersContactsId = this.getOfficers(false, false)
      .map(o => o.contactId)
      .filter(id => id !== null) as string[];
    this.relationshipContactList = await firstValueFrom(this.contactsApiService.getContactList(officersContactsId));

  }

  onSetParnerManagerChange(value: boolean) {
    this.setParnerManagerToCompany = value;
  }

  onSetChairPersonxChange(value: boolean) {
    this.setChairPersonAsSigningOfficer = value;
  }
}
